import { parseISO, format } from 'date-fns';
import type { Caregiver, NurseOperationalStatus, Assignment } from '../types/data.types';
import type { CalendarEvent } from './calendar-helpers';
import type {
  ApiCaregiver,
  CaregiverCalendarData,
  CalendarDay,
  CalendarShift,
  ShiftStatus
} from '../types/api.types';

/**
 * Generate a placeholder avatar with initials (similar to social media profiles)
 * @param name - Full name of the caregiver
 * @param gender - Gender of the caregiver ('male' or 'female')
 * @returns Data URI string with SVG placeholder avatar
 */
export function generatePlaceholderAvatar(name: string, gender: 'male' | 'female'): string {
  // Extract initials from name (up to 2 characters)
  const initials = name
    .split(' ')
    .map(part => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  // Choose background color based on gender - subtle gray tones
  const bgColor = gender === 'female' ? '#a1a1aa' : '#71717a'; // zinc-400 for female, zinc-500 for male
  const textColor = '#ffffff'; // white text for good contrast

  // Generate SVG with initials
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120">
      <rect width="120" height="120" fill="${bgColor}" rx="60" ry="60"/>
      <text
        x="50%"
        y="50%"
        dy="0.35em"
        text-anchor="middle"
        fill="${textColor}"
        font-family="system-ui, -apple-system, sans-serif"
        font-size="48"
        font-weight="600"
      >${initials}</text>
    </svg>
  `.trim();

  // Convert to data URI
  return `data:image/svg+xml;base64,${btoa(svg)}`;
}

/**
 * Derive operational status from API caregiver status
 */
export function deriveOperationalStatus(apiStatus: string): NurseOperationalStatus {
  if (apiStatus === 'ON_LEAVE') {
    return 'on_leave';
  }
  // For ACTIVE caregivers, default to 'available'
  // Note: 'on_assignment' status can be determined later when calendar data loads
  // INACTIVE/TERMINATED caregivers are filtered out at API level
  return 'available';
}

/**
 * Transform API caregiver response to Caregiver interface
 */
export function transformCaregiverFromAPI(apiCaregiver: ApiCaregiver): Caregiver {
  return {
    id: apiCaregiver.id,
    fullName: apiCaregiver.name,
    gender: apiCaregiver.gender,
    nationality: apiCaregiver.nationality || 'Not specified',
    city: apiCaregiver.city,
    status: apiCaregiver.status === 'ON_LEAVE' || apiCaregiver.status === 'TERMINATED'
      ? 'INACTIVE'
      : apiCaregiver.status,
    operationalStatus: deriveOperationalStatus(apiCaregiver.status),
    careTypes: [],  // Not in API
    utilizationPercentage: 0,  // Calculate from calendar data later
    phone: apiCaregiver.phone,
    dateOfBirth: apiCaregiver.dateOfBirth,
    languages: [],  // Not in API
    joiningDate: apiCaregiver.employment?.dateOfJoining,
    currentUtilizationHours: 0,  // Calculate from calendar data later
    maxWeeklyHours: 84,  // Default max hours
    email: apiCaregiver.email,
    avatar: apiCaregiver.profilePhoto || generatePlaceholderAvatar(apiCaregiver.name, apiCaregiver.gender),
  };
}

/**
 * Map API shift status to mock assignment status
 */
export function mapShiftStatus(apiStatus: ShiftStatus): Assignment['status'] {
  switch (apiStatus) {
    case 'SCHEDULED':
    case 'EXTENDED':
    case 'REDUCED':
    case 'OVERRIDE_REPLACEMENT':
    case 'SWAP_REPLACEMENT':
    case 'ADD_ON':
      return 'SCHEDULED';
    case 'SKIPPED':
    case 'SWAPPED_OUT':
      return 'CANCELLED';
    default:
      return 'SCHEDULED';
  }
}

/**
 * Derive shift type from time range
 */
export function deriveShiftType(startTime: string, endTime: string): 'DAY' | 'NIGHT' | 'FULL' {
  const start = parseInt(startTime.split(':')[0]);
  const end = parseInt(endTime.split(':')[0]);

  // Calculate duration (handle overnight shifts)
  let duration = end - start;
  if (duration < 0) {
    duration += 24;  // Overnight shift
  }

  // 20+ hours = Full day
  if (duration >= 20) {
    return 'FULL';
  }

  // Night shift: starts at 8 PM or later, or starts before 8 AM
  if (start >= 20 || start < 8) {
    return 'NIGHT';
  }

  // Default to day shift
  return 'DAY';
}

/**
 * Transform API calendar response to CalendarEvent array
 */
export function transformCalendarToEvents(
  calendarData: CaregiverCalendarData,
  caregiverId: string
): CalendarEvent[] {
  const events: CalendarEvent[] = [];

  if (!calendarData || !calendarData.calendar) {
    return events;
  }

  calendarData.calendar.forEach((day: CalendarDay) => {
    const date = day.date;

    // Handle leave days
    if (day.leave) {
      events.push({
        id: `leave-${caregiverId}-${date}`,
        caregiverId: caregiverId,
        title: 'On Leave',
        startDate: parseISO(date),
        endDate: parseISO(date),
        type: 'leave',
        status: 'SCHEDULED',
        color: '#9ca3af',
      });
    }

    // Handle shifts
    if (day.shifts && day.shifts.length > 0) {
      day.shifts.forEach((shift: CalendarShift, shiftIndex: number) => {
        events.push({
          id: `${caregiverId}-${date}-${shiftIndex}`,
          caregiverId: caregiverId,
          title: shift.customer?.recipientName || 'No patient name',
          startDate: parseISO(date),
          endDate: parseISO(date),
          type: 'assignment',
          status: mapShiftStatus(shift.status),
          shift: deriveShiftType(shift.startTime, shift.endTime),
          hours: shift.dailyHours,
          color: '',  // Assigned by Calendar component
          carePlanId: shift.carePlanId,
        });
      });
    }
  });

  return events;
}

/**
 * Calculate assignment phase (Day X of Y) for a given date
 * @param assignmentData - Care plan assignment data from API
 * @param currentDate - The date of the shift
 * @returns Object with currentDay and totalDays, or null if cannot be calculated
 */
export function calculateAssignmentPhase(
  assignmentData: any,
  currentDate: Date
): { currentDay: number; totalDays: number | null } | null {
  if (!assignmentData?.schedule) {
    return null;
  }

  const { startDate, endDate, daysOfWeek } = assignmentData.schedule;

  if (!startDate || !daysOfWeek || daysOfWeek.length === 0) {
    return null;
  }

  // Normalize dates to day-level (ignore time)
  const start = new Date(parseISO(startDate));
  start.setHours(0, 0, 0, 0);

  const current = new Date(currentDate);
  current.setHours(0, 0, 0, 0);

  // Count scheduled days from start up to AND INCLUDING current date
  let currentDay = 0;
  let date = new Date(start);

  while (date <= current) {
    const dayName = format(date, 'EEE').toUpperCase(); // SUN, MON, TUE, etc.
    if (daysOfWeek.includes(dayName)) {
      currentDay++;
    }
    date.setDate(date.getDate() + 1);
  }

  // If we have a shift on the current date, it must be at least Day 1
  // This handles edge cases where the shift exists but wasn't counted properly
  if (currentDay === 0) {
    currentDay = 1;
  }

  // Calculate total days if endDate exists
  let totalDays: number | null = null;
  if (endDate) {
    const end = parseISO(endDate);
    totalDays = 0;
    date = new Date(start);

    while (date <= end) {
      const dayName = format(date, 'EEE').toUpperCase();
      if (daysOfWeek.includes(dayName)) {
        totalDays++;
      }
      date.setDate(date.getDate() + 1);
    }
  }

  return { currentDay, totalDays };
}
